/** The two reads the fixed dashboards need that no other screen has a hook for yet
 * (ADR-0195: *a tile is a view, never a source*). Both endpoints exist and are tested on the
 * backend; this file only gives them a typed, honest client.
 *
 * - `GET /inventory/trends` - the window beside the prior one, per scope, site and category.
 * - `GET /evidence/counts` - documents by stage (ADR-0165).
 *
 * **Neither defaults to empty.** `unwrap` throws on a refusal or an empty body, so a dropped
 * request is a failed query the tile can say so about, never a quiet zero (CLAUDE.md, the nine read
 * hooks that once returned `[]`). A body that is not the shape a tile draws is also a failure.
 */

import { skipToken, useQuery } from "@tanstack/react-query";

import { api } from "./client";
import type { Scope2Method, Window } from "./queries";
import { unwrap } from "./queries";
import type { components } from "./schema";

export type Trends = components["schemas"]["TrendsOut"];
export type DocumentStageCounts = components["schemas"]["DocumentCountsOut"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function useInventoryTrends(window: Window | undefined, method: Scope2Method) {
  return useQuery({
    queryKey: ["dashboards", "trends", window?.period_start ?? "none", window?.period_end ?? "none", method] as const,
    queryFn: window
      ? async (): Promise<Trends> => {
          const trends = unwrap(
            await api.GET("/api/v1/inventory/trends", {
              params: {
                query: {
                  period_start: window.period_start,
                  period_end: window.period_end,
                  scope2_method: method,
                },
              },
            }),
          );
          if (!isRecord(trends) || !Array.isArray(trends.scopes) || !Array.isArray(trends.months)) {
            throw new Error("The comparison with the prior period came back in a shape this screen cannot read.");
          }
          return trends;
        }
      : skipToken,
  });
}

export function useDocumentStageCounts(enabled: boolean) {
  return useQuery({
    queryKey: ["dashboards", "document-stages"] as const,
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<DocumentStageCounts> => {
      const counts = unwrap(await api.GET("/api/v1/evidence/counts", { params: { query: {} } }));
      if (!isRecord(counts) || !Array.isArray(counts.stages)) {
        throw new Error("The document stages came back in a shape this screen cannot read.");
      }
      return counts;
    },
  });
}
